import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, FileText, XCircle, RotateCcw, Plus, Trash2, Pencil, ExternalLink } from "lucide-react";
import Attachments from "../components/Attachments";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const EMPTY = {
    prf_type: "regular",
    project_name: "",
    project_location: "",
    customer_code: "",
    customer_name: "",
    contractor: "",
    architect: "",
    developer: "",
    competitor: "",
    target_price: "",
    competitor_price: "",
    estimated_volume: "",
    schema_name: "",
    remarks: "",
    irregular_details: "",
    line_items: [],
};

const EMPTY_LINE = { item_code: "", item_name: "", size: "", quantity: "", srp2: "", offered_price: "" };

function statusClass(s) {
    if (s === "approved" || s === "converted") return "bg-green-50 text-green-700 border-green-200";
    if (s === "rejected") return "bg-red-50 text-red-700 border-red-200";
    if (s === "for_revision") return "bg-amber-50 text-amber-700 border-amber-200";
    if (s === "pre_quotation") return "bg-orange-50 text-orange-700 border-orange-200";
    if (s === "pending_bm_approval") return "bg-purple-50 text-purple-700 border-purple-200";
    return "bg-blue-50 text-blue-700 border-blue-200";
}

function StatusBadge({ status }) {
    return (
        <span className={`inline-flex px-2 py-1 rounded border text-xs font-medium ${statusClass(status)}`}>
            {(status || "submitted").replaceAll("_", " ")}
        </span>
    );
}

// Client-side mirror of the backend rule (offered price under SRP2 => needs PD approval).
function isBelowSrp2(lines) {
    return (lines || []).some((l) => {
        const s = Number(l.srp2 || 0), o = Number(l.offered_price || 0);
        return s > 0 && o > 0 && o < s;
    });
}

function withinEditWindow(prf) {
    if (!prf?.created_at) return false;
    const created = new Date(prf.created_at).getTime();
    if (Number.isNaN(created)) return false;
    const days = (Date.now() - created) / 86400000;
    return days <= 7;
}

export default function ProjectRegistrations() {
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [formMode, setFormMode] = useState(null); // null | "create" | "edit"
    const [editingId, setEditingId] = useState(null);
    const [selected, setSelected] = useState(null); // detail modal
    const [busy, setBusy] = useState(false);
    const { user } = useAuth();
    const nav = useNavigate();

    const isApprover = user?.role === "admin" || user?.role === "branch_manager" || user?.permissions?.includes("project_registration.approve");

    const load = async () => {
        const { data } = await api.get("/project-registrations");
        setRows(data);
    };
    useEffect(() => { load(); }, []);

    const summary = useMemo(() => ({
        total: rows.length,
        submitted: rows.filter((r) => r.status === "submitted").length,
        pre_quotation: rows.filter((r) => r.status === "pre_quotation").length,
        converted: rows.filter((r) => r.status === "converted").length,
        revision: rows.filter((r) => r.status === "for_revision").length,
    }), [rows]);

    const openCreate = () => { setForm(EMPTY); setEditingId(null); setFormMode("create"); };
    const openEdit = (prf) => {
        setForm({
            ...EMPTY, ...prf,
            target_price: prf.target_price ?? "", competitor_price: prf.competitor_price ?? "", estimated_volume: prf.estimated_volume ?? "",
            line_items: (prf.line_items || []).map((l) => ({ ...EMPTY_LINE, ...l })),
        });
        setEditingId(prf.id); setFormMode("edit"); setSelected(null);
    };

    const setLine = (i, patch) => setForm((f) => ({ ...f, line_items: f.line_items.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
    const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { ...EMPTY_LINE }] }));
    const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, j) => j !== i) }));

    const submitForm = async () => {
        const payload = {
            ...form,
            target_price: Number(form.target_price || 0),
            competitor_price: Number(form.competitor_price || 0),
            estimated_volume: Number(form.estimated_volume || 0),
            line_items: form.prf_type === "regular"
                ? form.line_items.filter((l) => (l.item_code || l.item_name)).map((l) => ({
                    ...l, quantity: Number(l.quantity || 0), srp2: Number(l.srp2 || 0), offered_price: Number(l.offered_price || 0),
                }))
                : [],
        };
        setBusy(true);
        try {
            if (formMode === "edit") {
                await api.put(`/project-registrations/${editingId}`, payload);
                toast.success("PRF updated");
            } else {
                await api.post("/project-registrations", payload);
                toast.success("Project Registration submitted");
            }
            setForm(EMPTY); setFormMode(null); setEditingId(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const act = async (id, action) => {
        const comment = window.prompt(action === "approve" ? "Approval comment (optional):" : "Comment:");
        if (comment === null) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/project-registrations/${id}/${action}`, { comment });
            if (action === "approve" && data?.quotation_no) toast.success(`Approved → converted to ${data.quotation_no}`);
            else toast.success(`PRF ${action === "reject" ? "sent back to pre-quotation" : action + "d"}`);
            setSelected(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const formBelow = isBelowSrp2(form.line_items);

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="project-registrations-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">Sales</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Project Registrations</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        PRF for project pricing. Regular = catalog items; Irregular = custom (free text). Approval converts to a quotation; below-SRP2 needs PD (admin) approval.
                    </p>
                </div>
                <button onClick={openCreate} className="h-9 inline-flex items-center justify-center gap-2 px-3 rounded-md bg-foreground text-background text-sm" data-testid="prf-create">
                    <FileText className="w-4 h-4" /> New PRF
                </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <SummaryCard label="Total" value={summary.total} />
                <SummaryCard label="Submitted" value={summary.submitted} />
                <SummaryCard label="For Revision" value={summary.revision} />
                <SummaryCard label="Pre-Quotation" value={summary.pre_quotation} />
                <SummaryCard label="Converted" value={summary.converted} />
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[900px]">
                        <thead>
                            <tr>
                                <th>Date Created</th>
                                <th>PRF No</th>
                                <th>Type</th>
                                <th>Project</th>
                                <th>Customer</th>
                                <th>Sales Agent</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No Project Registrations yet.</td></tr>
                            )}
                            {rows.map((r) => (
                                <tr key={r.id} onClick={() => setSelected(r)} className="cursor-pointer hover:bg-secondary/40" data-testid={`prf-row-${r.id}`}>
                                    <td className="text-xs text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                    <td className="font-mono text-xs">{r.prf_no}</td>
                                    <td>
                                        <span className="text-xs capitalize">{r.prf_type || "regular"}</span>
                                        {r.below_srp2 && <span className="ml-1 text-[10px] font-semibold uppercase text-orange-600">· below SRP2</span>}
                                    </td>
                                    <td>
                                        <div className="font-medium">{r.project_name}</div>
                                        <div className="text-xs text-muted-foreground">{r.project_location || "—"}</div>
                                    </td>
                                    <td>
                                        <div>{r.customer_name || r.customer_code || "—"}</div>
                                        <div className="text-xs text-muted-foreground">{r.schema_name || "—"}</div>
                                    </td>
                                    <td className="text-xs text-muted-foreground">{r.created_by_email}</td>
                                    <td><StatusBadge status={r.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail modal */}
            {selected && (
                <DetailModal
                    prf={selected}
                    user={user}
                    isApprover={isApprover}
                    busy={busy}
                    onClose={() => setSelected(null)}
                    onEdit={() => openEdit(selected)}
                    onAct={act}
                    onOpenQuotation={(qid) => nav(`/quotations/${qid}`)}
                />
            )}

            {/* Create / Edit form modal */}
            {formMode && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-auto p-5 space-y-4">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="sc-section-title">Project Registration Form</div>
                                <h2 className="text-lg font-semibold">{formMode === "edit" ? "Edit PRF" : "Create PRF"}</h2>
                            </div>
                            <div className="inline-flex rounded-md border border-border overflow-hidden">
                                {["regular", "irregular"].map((tp) => (
                                    <button key={tp} onClick={() => setForm({ ...form, prf_type: tp })}
                                        className={`px-3 py-1.5 text-sm capitalize ${form.prf_type === tp ? "bg-secondary font-medium" : "text-muted-foreground"}`}
                                        data-testid={`prf-type-${tp}`}>
                                        {tp}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input label="Project Name *" value={form.project_name} onChange={(v) => setForm({ ...form, project_name: v })} />
                            <Input label="Project Location *" value={form.project_location} onChange={(v) => setForm({ ...form, project_location: v })} />
                            <Input label="Customer Code" value={form.customer_code} onChange={(v) => setForm({ ...form, customer_code: v })} />
                            <Input label="Customer Name *" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} />
                            <Input label="Contractor" value={form.contractor} onChange={(v) => setForm({ ...form, contractor: v })} />
                            <Input label="Architect" value={form.architect} onChange={(v) => setForm({ ...form, architect: v })} />
                            <Input label="Developer" value={form.developer} onChange={(v) => setForm({ ...form, developer: v })} />
                            <Input label="Competitor" value={form.competitor} onChange={(v) => setForm({ ...form, competitor: v })} />
                            <Input label="Target Price" type="number" value={form.target_price} onChange={(v) => setForm({ ...form, target_price: v })} />
                            <Input label="Competitor Price" type="number" value={form.competitor_price} onChange={(v) => setForm({ ...form, competitor_price: v })} />
                            <Input label="Estimated Volume" type="number" value={form.estimated_volume} onChange={(v) => setForm({ ...form, estimated_volume: v })} />
                            <Input label="Schema / Company" value={form.schema_name} onChange={(v) => setForm({ ...form, schema_name: v })} />
                        </div>

                        {form.prf_type === "regular" ? (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Line Items (catalog) — no distributor price</div>
                                    <button onClick={addLine} className="h-7 px-2 rounded-md border border-border text-xs hover:bg-secondary inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add item</button>
                                </div>
                                {form.line_items.length === 0 && <div className="text-xs text-muted-foreground">Add at least one item.</div>}
                                {form.line_items.map((l, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                        <input className="sc-input h-8 col-span-2 text-xs" placeholder="Item code" value={l.item_code} onChange={(e) => setLine(i, { item_code: e.target.value })} />
                                        <input className="sc-input h-8 col-span-3 text-xs" placeholder="Item name" value={l.item_name} onChange={(e) => setLine(i, { item_name: e.target.value })} />
                                        <input className="sc-input h-8 col-span-2 text-xs" placeholder="Size" value={l.size} onChange={(e) => setLine(i, { size: e.target.value })} />
                                        <input className="sc-input h-8 col-span-1 text-xs text-right" type="number" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} />
                                        <input className="sc-input h-8 col-span-2 text-xs text-right" type="number" placeholder="SRP2" value={l.srp2} onChange={(e) => setLine(i, { srp2: e.target.value })} />
                                        <input className={`sc-input h-8 col-span-1 text-xs text-right ${Number(l.offered_price) > 0 && Number(l.offered_price) < Number(l.srp2) ? "border-orange-400" : ""}`} type="number" placeholder="Offered" value={l.offered_price} onChange={(e) => setLine(i, { offered_price: e.target.value })} />
                                        <button onClick={() => removeLine(i)} className="col-span-1 h-8 grid place-items-center rounded border border-border hover:bg-secondary"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                ))}
                                {formBelow && <div className="text-xs text-orange-600 font-medium">An offered price is below SRP2 → this PRF will require PD (admin) approval.</div>}
                            </div>
                        ) : (
                            <label className="block text-sm">
                                <span className="text-muted-foreground">Irregular details * (free text)</span>
                                <textarea className="sc-input mt-1 min-h-[110px]" value={form.irregular_details} onChange={(e) => setForm({ ...form, irregular_details: e.target.value })} placeholder="Describe the custom / non-catalog requirement…" />
                            </label>
                        )}

                        <label className="block text-sm">
                            <span className="text-muted-foreground">Remarks</span>
                            <textarea className="sc-input mt-1 min-h-[70px]" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
                        </label>

                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setFormMode(null); setEditingId(null); }} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={submitForm} disabled={busy} className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50" data-testid="prf-submit">
                                {formMode === "edit" ? "Save changes" : "Submit PRF"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function DetailModal({ prf, user, isApprover, busy, onClose, onEdit, onAct, onOpenQuotation }) {
    const canEdit = (prf.created_by_email === user?.email || user?.role === "admin")
        && ["submitted", "for_revision", "pre_quotation"].includes(prf.status)
        && withinEditWindow(prf);
    const canAct = isApprover && ["submitted", "for_revision", "pending_bm_approval"].includes(prf.status);
    const needsPd = prf.below_srp2 || prf.requires_pd_approval;
    const approveLabel = prf.status === "pending_bm_approval"
        ? "BM/OH Approve → Quotation"
        : (needsPd ? "PD Approve" : "Approve → Quotation");

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="sc-section-title">Project Registration</div>
                        <h2 className="text-lg font-semibold font-mono">{prf.prf_no}</h2>
                        <div className="text-sm text-muted-foreground mt-1 capitalize">{prf.prf_type || "regular"} · {prf.created_by_email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusBadge status={prf.status} />
                        <button onClick={onClose} className="h-9 px-3 rounded border border-border text-sm hover:bg-secondary">Close</button>
                    </div>
                </div>

                {needsPd && (
                    <div className="rounded-md border border-orange-200 bg-orange-50 text-orange-800 text-xs px-3 py-2">
                        {prf.status === "pending_bm_approval"
                            ? "Below SRP2 — PD approved. Awaiting BM / OH / Partner final approval on the below-SRP2 price."
                            : "Below SRP2 — needs PD (admin) approval first, then BM / OH / Partner, before it converts to a quotation."}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <Info label="Project" value={prf.project_name} />
                    <Info label="Location" value={prf.project_location} />
                    <Info label="Customer" value={prf.customer_name || prf.customer_code} />
                    <Info label="Schema" value={prf.schema_name} />
                    <Info label="Contractor" value={prf.contractor} />
                    <Info label="Architect" value={prf.architect} />
                    <Info label="Developer" value={prf.developer} />
                    <Info label="Competitor" value={prf.competitor} />
                    <Info label="Target Price" value={prf.target_price} />
                </div>

                {prf.prf_type === "irregular" ? (
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Irregular details</div>
                        <div className="text-sm whitespace-pre-wrap rounded-md border border-border p-3 bg-secondary/20">{prf.irregular_details || "—"}</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full sc-table min-w-[560px]">
                            <thead><tr><th>Item</th><th>Size</th><th className="text-right">Qty</th><th className="text-right">SRP2</th><th className="text-right">Offered</th></tr></thead>
                            <tbody>
                                {(prf.line_items || []).length === 0 && <tr><td colSpan={5} className="text-center py-4 text-muted-foreground">No line items.</td></tr>}
                                {(prf.line_items || []).map((l, i) => (
                                    <tr key={i}>
                                        <td><div className="font-mono text-xs">{l.item_code}</div><div className="text-xs text-muted-foreground">{l.item_name}</div></td>
                                        <td className="text-xs">{l.size || "—"}</td>
                                        <td className="sc-num">{Number(l.quantity || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                        <td className="sc-num">₱{Number(l.srp2 || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                        <td className={`sc-num ${Number(l.offered_price) > 0 && Number(l.offered_price) < Number(l.srp2) ? "text-orange-600 font-semibold" : ""}`}>₱{Number(l.offered_price || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {prf.remarks && <div className="text-sm"><span className="text-muted-foreground">Remarks: </span>{prf.remarks}</div>}

                <Attachments collection="project-registrations" docId={prf.id} editable={prf.created_by_email === user?.email || isApprover} />

                {prf.status === "converted" && prf.converted_quotation_id && (
                    <button onClick={() => onOpenQuotation(prf.converted_quotation_id)} className="h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary inline-flex items-center gap-2">
                        <ExternalLink className="w-4 h-4" /> Open quotation {prf.converted_quotation_no || ""}
                    </button>
                )}

                <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-border">
                    {canEdit && (
                        <button onClick={onEdit} className="h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary inline-flex items-center gap-1"><Pencil className="w-4 h-4" /> Edit (within 7 days)</button>
                    )}
                    {canAct && (
                        <>
                            <button onClick={() => onAct(prf.id, "approve")} disabled={busy} className="h-9 px-3 rounded-md bg-emerald-600 text-white text-sm inline-flex items-center gap-1 disabled:opacity-50"><CheckCircle2 className="w-4 h-4" /> {approveLabel}</button>
                            <button onClick={() => onAct(prf.id, "revise")} disabled={busy} className="h-9 px-3 rounded-md border border-border text-sm inline-flex items-center gap-1 disabled:opacity-50"><RotateCcw className="w-4 h-4" /> Revise</button>
                            <button onClick={() => onAct(prf.id, "reject")} disabled={busy} className="h-9 px-3 rounded-md text-red-600 border border-border text-sm inline-flex items-center gap-1 disabled:opacity-50"><XCircle className="w-4 h-4" /> Disapprove</button>
                        </>
                    )}
                </div>

                {(prf.approval_history || []).length > 0 && (
                    <div className="pt-2 border-t border-border">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">History</div>
                        <ul className="space-y-1">
                            {prf.approval_history.map((h, i) => (
                                <li key={i} className="text-xs flex flex-wrap gap-2">
                                    <span className="text-muted-foreground font-mono">{new Date(h.at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</span>
                                    <StatusBadge status={h.status} />
                                    <span className="text-muted-foreground">by {h.by}</span>
                                    {h.comment && <span className="italic text-muted-foreground">— {h.comment}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div>
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="font-medium mt-0.5">{value || "—"}</div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text" }) {
    return (
        <label className="block text-sm">
            <span className="text-muted-foreground">{label}</span>
            <input className="sc-input mt-1" type={type} value={value} onChange={(e) => onChange(e.target.value)} />
        </label>
    );
}

function SummaryCard({ label, value }) {
    return (
        <div className="sc-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold mt-1">{Number(value || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</div>
        </div>
    );
}
